import { z } from 'zod'
import type { InvoiceStatus } from './werkbonnen'

// ─── Intercompany-facturen (FO-419, batch-55) ────────────────────────────────
// Gemailde facturen tussen eigen BV's: badge "Intercompany" in het gewone
// facturenoverzicht (bewust dezelfde naam als de Intercompany-module), buiten
// de externe omzet-/kostencijfers, en patroonherkenning + concept-klaarzetten
// in de Intercompany-module. Gedeeld tussen de API (routes/facturen-
// intercompany.ts, routes/ic/patronen.ts, lib/intercompany-*.ts) en de
// backoffice-UI (/facturen, /facturen/[id], /ic/patronen).
//
// Copy-regels: het begrip is overal "Intercompany"; de richting wordt als
// `Intercompany: <afzender> -> <ontvanger>` getoond (pijl, geen em-dash); de
// term "interne stroom" komt in de UI nergens voor.

/** Vaste badge-tekst (identiek aan de modulenaam). */
export const INTERCOMPANY_BADGE_LABEL = 'Intercompany'
/** Optionele tooltip op de badge. */
export const INTERCOMPANY_BADGE_TOOLTIP = "Factuur tussen eigen BV's"

/** Richting-regel voor het factuurdetail: `Intercompany: A -> B`. */
export function formatIntercompanyRichting(afzender: string, ontvanger: string | null): string {
  return `Intercompany: ${afzender} -> ${ontvanger ?? 'onbekende BV'}`
}

export const INTERCOMPANY_FACTUUR_BRON = ['MAILIMPORT', 'HANDMATIG', 'PATROON'] as const
export type IntercompanyFactuurBron = (typeof INTERCOMPANY_FACTUUR_BRON)[number]

export const INTERCOMPANY_FACTUUR_BRON_LABELS: Record<IntercompanyFactuurBron, string> = {
  MAILIMPORT: 'Herkend bij de mailimport',
  HANDMATIG: 'Handmatig gemarkeerd',
  PATROON: 'Klaargezet door een patroon',
}

export const INTERCOMPANY_PATROON_STATUS = ['VOORGESTELD', 'ACTIEF', 'GESTOPT', 'AFGEWEZEN'] as const
export type IntercompanyPatroonStatus = (typeof INTERCOMPANY_PATROON_STATUS)[number]

export const INTERCOMPANY_PATROON_STATUS_LABELS: Record<IntercompanyPatroonStatus, string> = {
  VOORGESTELD: 'Voorgesteld',
  ACTIEF: 'Actief',
  GESTOPT: 'Gestopt',
  AFGEWEZEN: 'Afgewezen',
}

export const INTERCOMPANY_PATROON_STATUS_BADGE_VARIANT: Record<
  IntercompanyPatroonStatus,
  'info' | 'warning' | 'success' | 'danger' | 'secondary'
> = {
  VOORGESTELD: 'warning',
  ACTIEF: 'success',
  GESTOPT: 'secondary',
  AFGEWEZEN: 'secondary',
}

export const INTERCOMPANY_PATROON_RITME = ['MAANDELIJKS', 'KWARTAAL'] as const
export type IntercompanyPatroonRitme = (typeof INTERCOMPANY_PATROON_RITME)[number]

export const INTERCOMPANY_PATROON_RITME_LABELS: Record<IntercompanyPatroonRitme, string> = {
  MAANDELIJKS: 'Maandelijks',
  KWARTAAL: 'Per kwartaal',
}

/**
 * Beoordeling van één verwachte periode van een patroon (op lezen berekend,
 * nooit opgeslagen): het systeem gokt niet en boekt niets zelf.
 */
export const INTERCOMPANY_PERIODE_STATUS = [
  'GEBOEKT',
  'CONCEPT',
  'AFWIJKEND_BEDRAG',
  'ONTBREEKT',
  'VERWACHT',
] as const
export type IntercompanyPeriodeStatus = (typeof INTERCOMPANY_PERIODE_STATUS)[number]

export const INTERCOMPANY_PERIODE_STATUS_LABELS: Record<IntercompanyPeriodeStatus, string> = {
  GEBOEKT: 'Geboekt',
  CONCEPT: 'Concept klaargezet',
  AFWIJKEND_BEDRAG: 'Bedrag wijkt af',
  ONTBREEKT: 'Blijft uit',
  VERWACHT: 'Verwacht',
}

export const INTERCOMPANY_PERIODE_STATUS_BADGE_VARIANT: Record<
  IntercompanyPeriodeStatus,
  'info' | 'warning' | 'success' | 'danger' | 'secondary'
> = {
  GEBOEKT: 'success',
  CONCEPT: 'info',
  AFWIJKEND_BEDRAG: 'warning',
  ONTBREEKT: 'danger',
  VERWACHT: 'secondary',
}

/**
 * Drempels van de patroonherkenning (aanname A2: conservatief, liever een
 * patroon missen dan een verkeerd voorstel). Eén bron voor de API-herkenning
 * én de uitleg in de UI.
 */
export const INTERCOMPANY_PATROON_DREMPELS = {
  /** Minimaal aantal opeenvolgende, passende facturen vóór een voorstel. */
  minHerkenningen: 3,
  /** Toegestane afwijking (%) rond de mediaan om als "vergelijkbaar bedrag" te gelden; ook de default-tolerantie op een nieuw patroon. */
  bedragTolerantiePct: 5,
  /** Speling (dagen) op het interval tussen twee opeenvolgende facturen. */
  ritmeTolerantieDagen: 7,
  /** Hoe ver (maanden) de herkenning terugkijkt. */
  terugkijkMaanden: 14,
  /** Dagen na de klaarzetdag waarna een uitblijvende factuur als "blijft uit" telt. */
  ontbreektNaDagen: 7,
} as const

// ─── DTO's ───────────────────────────────────────────────────────────────────

export interface IntercompanyBvRefDTO {
  id: string
  name: string
}

/** Eén patroon in de lijst (/ic/patronen). */
export interface IntercompanyPatroonRowDTO {
  id: string
  status: IntercompanyPatroonStatus
  ritme: IntercompanyPatroonRitme
  afzender: IntercompanyBvRefDTO
  ontvanger: IntercompanyBvRefDTO
  /** Bedragen als string (Decimal-conventie; de UI formatteert). */
  bedragExcl: string
  btwPercentage: string
  bedragTolerantiePct: string
  omschrijving: string | null
  dagVanPeriode: number
  herkendAantal: number
  laatsteConceptPeriode: string | null
  voorgesteldOp: string
  geaccepteerdOp: string | null
  gestoptOp: string | null
  stopReden: string | null
  /** Aantal gekoppelde facturen (badge-doorklik komt hier terecht). */
  facturenAantal: number
  /** Beoordeling van de lopende periode (null zolang het patroon niet actief is). */
  huidigePeriode: { periode: string; label: string; status: IntercompanyPeriodeStatus } | null
  /** Aantal periodes met een afwijking (bedrag wijkt af of blijft uit). */
  afwijkingenAantal: number
}

export interface IntercompanyPatroonFactuurRefDTO {
  id: string
  invoiceNumber: string | null
  conceptNumber: string | null
  status: InvoiceStatus
  subtotal: string
  total: string
  invoiceDate: string | null
  intercompanyBron: IntercompanyFactuurBron | null
}

/** Eén verwachte periode met de facturen die erin vallen. */
export interface IntercompanyPatroonPeriodeDTO {
  /** `YYYY-MM` (maandelijks) of `YYYY-Qn` (kwartaal). */
  periode: string
  /** Leesbaar label, bv. "augustus 2026" of "Q3 2026". */
  label: string
  status: IntercompanyPeriodeStatus
  verwachtBedragExcl: string
  /** Afwijking (%) van het geboekte bedrag t.o.v. het referentiebedrag; null zonder factuur. */
  afwijkingPct: string | null
  facturen: IntercompanyPatroonFactuurRefDTO[]
}

export interface IntercompanyPatroonDetailDTO extends IntercompanyPatroonRowDTO {
  periodes: IntercompanyPatroonPeriodeDTO[]
  facturen: IntercompanyPatroonFactuurRefDTO[]
  drempels: typeof INTERCOMPANY_PATROON_DREMPELS
}

export interface IntercompanyPatroonListMeta {
  total: number
  page: number
  pageSize: number
  perStatus: Record<IntercompanyPatroonStatus, number>
}

/** Antwoord van POST /ic/patronen/herken. */
export interface IntercompanyPatroonHerkenResultDTO {
  /** Aantal nieuwe voorstellen. */
  voorgesteld: number
  /** Aantal BV-paren dat is onderzocht. */
  parenOnderzocht: number
}

/** Antwoord van POST /ic/patronen/:id/concept-nu. */
export interface IntercompanyPatroonConceptResultDTO {
  periode: string
  /** Aangemaakte concept-factuur, of null wanneer er voor deze periode al één is. */
  invoiceId: string | null
  alAanwezig: boolean
}

// ─── Request-schemas (gedeeld form + API, zie CLAUDE.md Validatie) ───────────

const bedragSchema = z
  .number({ error: 'Vul een bedrag in' })
  .finite('Vul een bedrag in')
  .min(0, 'Bedrag kan niet negatief zijn')
  .max(99_999_999.99, 'Bedrag is te groot')

const percentageSchema = z
  .number({ error: 'Vul een percentage in' })
  .min(0, 'Percentage tussen 0 en 100')
  .max(100, 'Percentage tussen 0 en 100')

const dagVanPeriodeSchema = z
  .number({ error: 'Vul een dag in' })
  .int('Vul een hele dag in')
  .min(1, 'Dag tussen 1 en 28')
  .max(28, 'Dag tussen 1 en 28')

export const intercompanyPatroonListQuerySchema = z.object({
  status: z.enum(INTERCOMPANY_PATROON_STATUS).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
})

/** Body van POST /ic/patronen/:id/accepteren (alles optioneel: defaults van het voorstel gelden). */
export const intercompanyPatroonAccepterenSchema = z.object({
  ritme: z.enum(INTERCOMPANY_PATROON_RITME).optional(),
  dagVanPeriode: dagVanPeriodeSchema.optional(),
  bedragTolerantiePct: percentageSchema.optional(),
})
export type IntercompanyPatroonAccepterenInput = z.infer<typeof intercompanyPatroonAccepterenSchema>

/** Body van PATCH /ic/patronen/:id (aanpassen van een patroon). */
export const intercompanyPatroonUpdateSchema = z
  .object({
    ritme: z.enum(INTERCOMPANY_PATROON_RITME).optional(),
    bedragExcl: bedragSchema.optional(),
    btwPercentage: percentageSchema.optional(),
    bedragTolerantiePct: percentageSchema.optional(),
    dagVanPeriode: dagVanPeriodeSchema.optional(),
    omschrijving: z.string().trim().max(500, 'Maximaal 500 tekens').nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Geen wijzigingen opgegeven' })
export type IntercompanyPatroonUpdateInput = z.infer<typeof intercompanyPatroonUpdateSchema>

/** Body van POST /ic/patronen/:id/stoppen en /afwijzen. */
export const intercompanyPatroonRedenSchema = z.object({
  reden: z.string().trim().max(500, 'Maximaal 500 tekens').optional(),
})
export type IntercompanyPatroonRedenInput = z.infer<typeof intercompanyPatroonRedenSchema>

/**
 * Body van POST /invoices/:id/intercompany en POST /inkoop/facturen/:id/intercompany:
 * markering zetten (met tegenpartij-BV) of verwijderen, altijd met audit.
 */
export const intercompanyMarkeringSchema = z
  .object({
    isIntercompany: z.boolean({ error: 'Geef aan of dit een intercompany-factuur is' }),
    /** Verkoop: de ontvangende BV. Inkoop: de verzendende BV. Verplicht bij markeren. */
    tegenpartijEntityId: z.string().min(1).nullable().optional(),
    reden: z.string().trim().max(500, 'Maximaal 500 tekens').nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.isIntercompany && !v.tegenpartijEntityId) {
      ctx.addIssue({
        code: 'custom',
        path: ['tegenpartijEntityId'],
        message: 'Kies de andere eigen BV van deze factuur',
      })
    }
  })
export type IntercompanyMarkeringInput = z.infer<typeof intercompanyMarkeringSchema>

/** Antwoord van de markeer-routes. */
export interface IntercompanyMarkeringResultDTO {
  id: string
  isIntercompany: boolean
  intercompanyTegenpartij: IntercompanyBvRefDTO | null
  /** Verkoop: het patroon waar de factuur (nu) bij hoort. */
  intercompanyPatroonId: string | null
}
